import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useApp } from "../../design/AppContext";
import { hasPermission } from "../../utils/permission";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { MdErrorOutline, MdWarningAmber } from "react-icons/md";
import { Banner, Disclosure, Field, PasswordField } from "../../components/mui";
import { PageLoading } from "../../components/loading";

/**
 * 新增／編輯人員（設計稿 #screen-account-new，el-pm-template_4.html:2374-2434）
 *
 * 同一支檔案接兩條路由：
 *   · /Setting/Admin/New        —— 開新帳號
 *   · /Setting/Admin/:account   —— 改既有帳號
 * 舊版是把編輯表單就地展開在清單列底下，一次只能看一個人、而且展開之後
 * 整條清單會跳動。改成獨立網址之後可以直接把連結貼給人。
 *
 * ── 設計稿有、但後端沒有端點的兩顆按鈕（整顆不渲染，硬規則 2 與 10）──────
 *   · L2427「先把他的工項轉給別人」—— 全庫沒有「把某人名下的工項批次轉給
 *     另一個人」的端點。/Task 的更新一次只能改一件，而且沒有「列出某人名下
 *     所有工項」的查詢（/MyTask/List 只回登入者自己的）。
 *     畫成停用狀態會讓人以為是權限不足或暫時壞掉，所以整顆不畫。
 *   · 設計稿 L2402 是一顆「重設他的密碼」按鈕（按下去就重設）。
 *     後端沒有「重設成隨機密碼並寄給他」這種端點 —— 系統也還不會寄信。
 *     但 PutAdmin 本身吃 Password 欄位（填了就換成那組），這是既有功能，
 *     刪掉就等於管理員再也沒辦法幫忘記密碼的人開門（而 /Auth/ForgotPassword
 *     那一頁正是叫使用者來找管理員）。所以改成一個輸入欄位：
 *     管理員自己決定臨時密碼，當面或用電話告訴他（硬規則 8）。
 *
 * ── 設計稿有、但後端完全沒有這個概念的區塊 ────────────────────────
 *   · L2405-2415「可以看哪些專案」的勾選清單 —— ReqPostAdmin / ReqPutAdmin
 *     只有 Account / Password / Name / Email / Activation / RoleId 六個欄位，
 *     沒有任何「這個人可以看哪些專案」的欄位，admin 與 project 之間也沒有
 *     對應的資料表。勾了不會存、下次進來會全部跑掉，所以整塊不畫。
 *     （專案成員是另一回事：那是在專案詳情裡加人，語意不同，不要拿來冒充。）
 *
 * 整頁只有「存起來」是 contained 主要按鈕（硬規則 1）；停用是 error 色的 outlined。
 */

/** 卡片寬度照設計稿 L2379 */
const CARD = { mt: 3, maxWidth: 680 };

/**
 * 與後端 AuthService.ValidateNewPassword 同一套規則。
 * 規則逐字沿用 components/auth/ChangePasswordDialog.js 的 checkPassword，
 * 三個入口（改自己的、管理員幫人設、這裡）不要各寫一套。
 */
function checkPassword(pwd, account) {
  if (!pwd) return "請輸入密碼";
  if (pwd.length < 8) return "長度至少需 8 個字元";
  if (!pwd.trim()) return "不可僅包含空白";
  if (/^\d+$/.test(pwd)) return "不可全為數字";
  if (account && pwd.toLowerCase().includes(String(account).toLowerCase())) return "不可包含帳號";
  return "";
}

/** 停用／重新啟用的確認視窗。Esc 與點遮罩關閉由 Dialog 的 onClose 處理 */
function ConfirmDialog({ title, body, confirmText, busy, onCancel, onConfirm }) {
  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth role="alertdialog" aria-labelledby="adm-dlg-title">
      <DialogTitle id="adm-dlg-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{body}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant="outlined" type="button" onClick={onCancel}>
          取消
        </Button>
        <Button variant="contained" color="error" type="button" onClick={onConfirm} aria-disabled={busy} loading={busy} loadingPosition="start">
          {busy ? "處理中…" : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConfirmDialog.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  confirmText: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
};
ConfirmDialog.defaultProps = { busy: false, onCancel: () => {}, onConfirm: () => {} };

function SettingAdminEdit(props) {
  const navigate = useNavigate();
  const params = useParams();
  const { toast } = useApp();
  const { adminList, roleList, GET_AdminList, GET_RoleList, POST_Admin, PUT_Admin, canSeeRoles } = props;

  /* 路由沒有 :account 就是新增。/Setting/Admin/New 走的是另一條 route，
     那條沒有參數，所以這裡拿到的是 undefined */
  const account = params.account ? decodeURIComponent(params.account) : "";
  const isNew = !account;

  const [form, setForm] = useState({ Account: "", Name: "", Email: "", RoleId: "", Password: "" });
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState("");
  const [touched, setTouched] = useState(false);
  const [asking, setAsking] = useState(false);
  /* 停用區的開合：標頭右側照舊版寫出「展開」或「收合」 */
  const [dangerOpen, setDangerOpen] = useState(false);

  useEffect(() => {
    GET_AdminList(null, null);
    GET_RoleList(null, null);
  }, [GET_AdminList, GET_RoleList]);

  const roleOptions = useMemo(() => (Array.isArray(roleList) ? roleList : []), [roleList]);

  /*
   * 後端沒有「讀單一帳號」的端點，只有 /Auth/Admin/List。
   * 從清單裡撈出這一筆是目前唯一的辦法，不是偷懶。
   */
  const current = useMemo(() => {
    if (isNew) return null;
    const list = Array.isArray(adminList) ? adminList : [];
    return (
      list.find((a) => String(_.get(a, "account", _.get(a, "Account", ""))) === account) || null
    );
  }, [adminList, account, isNew]);

  /* 資料回來之後鋪一次表單；使用者已經在改就不要覆蓋掉他打到一半的內容 */
  useEffect(() => {
    if (isNew || loaded || !current) return;
    const one = _.get(current, "role", null);
    const arr = _.get(current, "roles", null);
    const r = one || (Array.isArray(arr) && arr.length ? arr[0] : null);
    setForm({
      Account: account,
      Name: _.get(current, "name", _.get(current, "Name", "")) || "",
      Email: _.get(current, "email", _.get(current, "Email", "")) || "",
      RoleId: String(_.get(r, "id", _.get(r, "Id", "")) || ""),
      Password: "",
    });
    setLoaded(true);
  }, [current, isNew, loaded, account]);

  const active = _.get(current, "activation", _.get(current, "Activation", true)) !== false;
  const createAt = _.get(current, "create_at", _.get(current, "CreateAt", null));
  const displayName = _.get(current, "name", _.get(current, "Name", "")) || account;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  /* 欄位層級的錯誤。送出前不吵（touched），送出後才一次把沒填的指出來 */
  const nameBad = touched && !form.Name.trim();
  const accountBad = touched && isNew && !form.Account.trim();
  const roleBad = touched && !form.RoleId;
  const pwdBad = (() => {
    if (isNew) return touched || form.Password ? checkPassword(form.Password, form.Account) : "";
    // 編輯時密碼是選填：留空＝不更動，有填才檢查
    return form.Password ? checkPassword(form.Password, account) : "";
  })();

  const submit = (e) => {
    e.preventDefault();
    if (busy) return;
    setTouched(true);

    if (isNew && !form.Account.trim()) {
      setBanner("請填寫登入帳號（使用者登入時輸入的帳號）。");
      return;
    }
    if (!form.Name.trim()) {
      setBanner("請填寫姓名，工項的指派人與留言皆會顯示此名稱。");
      return;
    }
    if (!form.RoleId) {
      setBanner("請選擇角色，角色決定該使用者可檢視的內容與可執行的操作。");
      return;
    }
    const bad = isNew
      ? checkPassword(form.Password, form.Account)
      : form.Password
      ? checkPassword(form.Password, account)
      : "";
    if (bad) {
      setBanner(`密碼${bad}。`);
      return;
    }

    setBanner("");
    if (isNew) {
      POST_Admin(
        {
          Account: form.Account.trim(),
          Password: form.Password,
          Name: form.Name.trim(),
          Email: form.Email.trim(),
          RoleId: Number(form.RoleId),
        },
        () => {
          toast(`已建立 ${form.Name.trim()} 的帳號`);
          navigate("/Setting/Admin");
        },
        setBusy
      );
      return;
    }

    /*
     * 後端 PutAdmin 會先刪掉舊的 admin_role 再依 RoleId 重建，
     * 所以 RoleId 一定要帶著 —— 少帶一次就會把他的角色洗掉。
     * Activation 同理：這裡不動它，照現況帶回去，停用另外走下面的摺疊區。
     */
    PUT_Admin(
      {
        Account: account,
        Name: form.Name.trim(),
        Email: form.Email.trim(),
        Activation: active,
        Password: form.Password || null,
        RoleId: Number(form.RoleId),
      },
      () => {
        toast(form.Password ? "已儲存，密碼已一併變更" : "已儲存");
        navigate("/Setting/Admin");
      },
      setBusy
    );
  };

  /**
   * 停用／重新啟用：沒有專用端點，走 PutAdmin 把 Activation 翻過來。
   *
   * RoleId 一定要帶一個有效值 —— PutAdmin 會先刪掉舊的 admin_role 再依
   * RoleId 重建，帶 0 或不帶就會在 admin_role 塞一筆指向不存在的角色。
   * 所以沒有角色的人先擋下來，請管理員補完角色再停用。
   */
  const toggleActivation = () => {
    if (busy) return;
    if (!form.RoleId) {
      setAsking(false);
      setBanner("該使用者尚未指派角色。請先選擇角色並按「儲存」後再停用，否則其角色設定將被清除。");
      return;
    }
    PUT_Admin(
      {
        Account: account,
        Name: form.Name.trim() || displayName,
        Email: form.Email.trim(),
        Activation: !active,
        RoleId: Number(form.RoleId),
      },
      () => {
        setAsking(false);
        toast(active ? "已停用此帳號" : "已重新啟用此帳號");
        GET_AdminList(null, null);
        navigate("/Setting/Admin");
      },
      setBusy
    );
  };

  /* 編輯模式下清單還沒回來時不要先畫一張空表單 —— 使用者會開始打字，
     資料回來之後又被蓋掉 */
  if (!isNew && !current) {
    return (
      <>
        <Typography variant="h1" component="h1">
          編輯人員
        </Typography>
        <PageLoading />
      </>
    );
  }

  return (
    <>
      <Typography variant="h1" component="h1">
        {isNew ? "新增人員" : `編輯人員：${displayName}`}
      </Typography>

      {/* 建立日期是後端真的回得出來的（AdminList 的 create_at）；
          設計稿 L2377 同一行的「上次登入」後端沒有，整段不寫 */}
      {!isNew && createAt && dayjs(createAt).isValid() ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          建立於 {dayjs(createAt).format("YYYY 年 M 月 D 日")}
        </Typography>
      ) : null}

      {/* 停用中的帳號要在最上面講清楚，不然管理員會以為只是資料沒回來（硬規則 4） */}
      {!isNew && !active ? (
        <Banner kind="warn" icon={<MdErrorOutline />} sx={{ mt: 2, maxWidth: 680 }}>
          <strong>此帳號已停用　</strong>該使用者目前無法登入。其回報、留言、審核紀錄均保留，
          名下工項也維持不變。如需恢復，請按下方的「重新啟用此帳號」。
        </Banner>
      ) : null}

      <Card component="section" sx={CARD}>
        <CardContent>
          <Stack component="form" spacing={2} onSubmit={submit}>
            {banner ? (
              <Banner kind="attention" icon={<MdWarningAmber />} role="alert">
                {banner}
              </Banner>
            ) : null}

            {/* 帳號：新增時可以填，開好之後不能改（後端用它當主鍵） */}
            {isNew ? (
              <Field
                label="登入帳號"
                htmlFor="u-account"
                mark="req"
                help="使用者登入時輸入的帳號。建立後無法修改，請確認後再送出。"
                error={accountBad ? "請填寫登入帳號。" : null}
              >
                <TextField placeholder="例如 ming.huang" id="u-account" fullWidth value={form.Account} onChange={set("Account")} error={accountBad} />
              </Field>
            ) : (
              <Field label="登入帳號" help="帳號建立後無法修改；如需更換，請另行建立新帳號。">
                <Typography sx={{ fontWeight: 600 }}>{account}</Typography>
              </Field>
            )}

            <Field
              label="姓名"
              htmlFor="u-name"
              mark="req"
              help="工項的指派人、會議出席名單與留言皆會顯示此名稱。"
              error={nameBad ? "請填寫姓名。" : null}
            >
              <TextField placeholder="例如 黃小明" id="u-name" fullWidth value={form.Name} onChange={set("Name")} error={nameBad} />
            </Field>

            {/*
              設計稿 L2387 寫「這就是他的登入帳號」，但這套系統的登入帳號是上面
              那個獨立欄位，Email 只是聯絡方式（後端 Email 也是可為 null）。
              照抄那句話會讓管理員以為 Email 打錯他就登不進來了。
            */}
            {/*
              「目前系統不會寄信，所以不填也不影響他使用」已經不成立了：
              SMTP 一啟用，通知就會依他自己的偏好寄出，每天那封到期提醒
              （他負責的工項快到期／已逾期、他所屬專案的里程碑快來不及）也走同一條路。
              沒填 Email 的人不是「沒差」，而是只剩站內通知 —— 這件事要講出來。
              用字與「設定 → 偏好設定」裡自己那一格同一套。
            */}
            <Field
              label="公司 Email"
              htmlFor="u-mail"
              help="僅供聯絡使用，並非登入帳號，未填寫不影響登入。系統通知信將寄送至此信箱；未填寫時該使用者僅接收站內通知。"
            >
              <TextField placeholder="例如 name@company.com" id="u-mail" fullWidth value={form.Email} onChange={set("Email")} />
            </Field>

            <Field
              label="角色"
              htmlFor="u-role"
              mark="req"
              help="角色決定該使用者可檢視的內容與可執行的操作。每位使用者僅能指派一個角色，日後可變更。"
              error={roleBad ? "請選擇角色。" : null}
              footer={
                /* 沒有 role 權限的人點進去只會撞到「你沒有權限」，那一顆就不畫 */
                canSeeRoles ? (
                  <Box sx={{ mt: 1 }}>
                    <Button variant="text" type="button" onClick={() => navigate("/Setting/Roles")}>
                      檢視各角色權限
                    </Button>
                  </Box>
                ) : null
              }
            >
              <TextField
                select
                id="u-role"
                fullWidth
                value={form.RoleId}
                onChange={set("RoleId")}
                error={roleBad}
                slotProps={{ select: { native: true } }}
              >
                <option value="">請選擇角色</option>
                {roleOptions.map((r) => {
                  const id = _.get(r, "id");
                  const nm = _.get(r, "name", "");
                  /* 職責寫在選項裡（設計稿 L2392-2398）。文字取自角色自己的備註欄，
                     沒寫備註就只顯示名稱 —— 不要幫它編一句職責出來（硬規則 10） */
                  const note = String(_.get(r, "note", "") || "").trim();
                  return (
                    <option key={id} value={id}>
                      {note ? `${nm} — ${note}` : nm}
                    </option>
                  );
                })}
              </TextField>
            </Field>

            <Field
              label={isNew ? "初始密碼" : "重設密碼"}
              htmlFor="u-pwd"
              mark={isNew ? "req" : ""}
              help={
                isNew
                  ? "至少 8 個字元，不可全為數字，亦不可包含帳號。系統不會寄送密碼，請以當面或電話方式告知對方，並請對方登入後自行變更。"
                  : "留空表示不變更。填寫後將立即套用新密碼，系統不會通知對方，請自行告知。"
              }
              error={pwdBad ? `密碼${pwdBad}。` : null}
            >
              <PasswordField
                placeholder="至少 8 個字元"
                id="u-pwd"
                autoComplete="new-password"
                fullWidth
                value={form.Password}
                onChange={set("Password")}
                error={!!pwdBad}
              />
            </Field>

            <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
              <Button variant="contained" type="submit" aria-disabled={busy} loading={busy} loadingPosition="start">
                {busy ? "儲存中…" : "儲存"}
              </Button>
              <Button variant="text" type="button" onClick={() => navigate("/Setting/Admin")}>
                取消
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* ---------- 停用這個帳號（設計稿 L2420-2433） ---------- */}
      {/* 新增中的人還不存在，沒有東西可以停用；停用是後果很大的動作，
          所以收在摺疊區裡，不跟「存起來」放在同一排（硬規則 2） */}
      {!isNew ? (
        <Disclosure
          title={active ? "停用此帳號" : "重新啟用此帳號"}
          count={active ? "（停用後將無法登入）" : "（啟用後即可登入）"}
          summaryExtra={dangerOpen ? "收合" : "展開"}
          open={dangerOpen}
          onToggle={setDangerOpen}
          sx={CARD}
        >
          <Typography>
            {active
              ? "停用後該使用者將無法登入，但其回報、留言、審核紀錄均會保留，名下工項也維持不變。已登入的裝置須待登入時效到期後才會被阻擋。"
              : "重新啟用後，該使用者可使用原帳號密碼登入，角色與權限與停用前相同。"}
          </Typography>
          {/*
            設計稿 L2427 的「先把他的工項轉給別人」在這裡整顆不畫：
            後端沒有批次轉派的端點（理由見檔頭）。要轉派目前只能一件一件
            從工項詳情改指派人。
          */}
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
            <Button variant="outlined" color="error" type="button" onClick={() => setAsking(true)}>
              {active ? "停用此帳號" : "重新啟用此帳號"}
            </Button>
          </Stack>
        </Disclosure>
      ) : null}

      {asking ? (
        <ConfirmDialog
          title={active ? `確定要停用 ${displayName} 的帳號嗎？` : `確定要重新啟用 ${displayName} 的帳號嗎？`}
          body={
            active
              ? "停用後該使用者將無法登入。其歷史紀錄均會保留，名下工項維持不變，日後可隨時重新啟用。"
              : "啟用後該使用者可使用原帳號密碼登入，可檢視的內容與停用前相同。"
          }
          confirmText={active ? "確定停用" : "確定啟用"}
          busy={busy}
          onCancel={() => setAsking(false)}
          onConfirm={toggleActivation}
        />
      ) : null}
    </>
  );
}

const mapStateToProps = (state) => ({
  adminList: _.get(state, "Admin.List", []),
  roleList: _.get(state, "Role.List", []),
  /* 角色權限表那條路由要 role 權限（resources/menu.js 的 set_role） */
  canSeeRoles: hasPermission("role"),
});

const mapDispatchToProps = (dispatch) => ({
  GET_AdminList: (callback, loading) => dispatch({ type: "GET_AdminList", callback, loading }),
  GET_RoleList: (callback, loading) => dispatch({ type: "GET_RoleList", callback, loading }),
  POST_Admin: (payload, callback, loading) => dispatch({ type: "POST_Admin", payload, callback, loading }),
  PUT_Admin: (payload, callback, loading) => dispatch({ type: "PUT_Admin", payload, callback, loading }),
});

SettingAdminEdit.propTypes = {
  adminList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  roleList: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  canSeeRoles: PropTypes.bool,
  GET_AdminList: PropTypes.func,
  GET_RoleList: PropTypes.func,
  POST_Admin: PropTypes.func,
  PUT_Admin: PropTypes.func,
};

SettingAdminEdit.defaultProps = {
  adminList: [],
  roleList: [],
  canSeeRoles: false,
  GET_AdminList: () => {},
  GET_RoleList: () => {},
  POST_Admin: () => {},
  PUT_Admin: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(SettingAdminEdit);
